import React, { useEffect, useState } from "react";
import styles from "./Menu.module.scss";
import type { MenuItemType, MenuType } from "../../../types/baseTypes";
import MenuItem from "./MenuItem/MenuItem";
import { useKeyboardNavigation } from "./keyboard-navigation";
import type { Post } from "@aleksundshantu/gp-types/client";
import type { OptionsNavigationType } from "@/types/OptionType";
import type {
  MegaMenuHighlightsType,
  MegaMenuKeyType,
} from "./MegaMenu/MegaMenu";
import { CustomLink } from "@/components/CustomLink";
import { useAppContext } from "@/context/AppContext";

interface MenuProps {
  menu: MenuType;
  pageData?: Post;
  themeNavigation?: OptionsNavigationType;
  megaMenuHighlights: MegaMenuHighlightsType;
  topMenu: MenuType | null;
  onMegaMenuOpenChange?: (isOpen: boolean) => void;
}

export type MenuParentType = MenuItemType | undefined;

const megaMenuOptionKeys: MegaMenuKeyType[] = [
  "hotel",
  "health",
  "sport",
  "kulinarik",
];

export const Menu = ({
  menu,
  pageData,
  themeNavigation,
  megaMenuHighlights,
  topMenu,
  onMegaMenuOpenChange,
}: MenuProps) => {
  const { setOpenMenu } = useAppContext();
  const [currentSubmenu, setCurrentSubmenu] = useState<MenuParentType>();
  const childMenuItems = menu.items.filter(
    (item) => item.child_items && item.child_items.length > 0,
  );
  const currentSubmenuIndex = currentSubmenu
    ? childMenuItems.findIndex((item) => item.ID === currentSubmenu.ID)
    : -1;
  const currentMegaMenuKey =
    currentSubmenuIndex >= 0
      ? megaMenuOptionKeys[currentSubmenuIndex]
      : undefined;
  const isMegaMenuOpen = !!(
    currentMegaMenuKey &&
    (themeNavigation?.[currentMegaMenuKey] ||
      currentMegaMenuKey === "kulinarik")
  );

  const { menuRef, items, focusedItem, keyDown, controlMenu } =
    useKeyboardNavigation({
      menu,
      setCurrentSubmenu,
      currentSubmenu,
      type: "menu",
      subMenu: undefined,
    });

  useEffect(() => {
    const handleFocusChange = (event: FocusEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setCurrentSubmenu(undefined);
      }
    };

    document.addEventListener("focusin", handleFocusChange);

    return () => {
      document.removeEventListener("focusin", handleFocusChange);
    };
  }, [menuRef]);

  useEffect(() => {
    if (!currentSubmenu) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const navbar = menuRef.current?.closest("nav.navbar");

      if (menuRef.current?.contains(target)) return;

      if (!navbar?.contains(target)) {
        setCurrentSubmenu(undefined);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [currentSubmenu, menuRef]);

  useEffect(() => {
    onMegaMenuOpenChange?.(isMegaMenuOpen);

    return () => {
      onMegaMenuOpenChange?.(false);
    };
  }, [isMegaMenuOpen, onMegaMenuOpenChange]);

  return (
    <>
      <ul
        ref={menuRef}
        role="menubar"
        aria-label="Hauptmenü"
        className={`${styles["main-menu"]}`}
        onKeyDown={keyDown}
      >
        {menu &&
          Array.isArray(menu.items) &&
          menu.items.map((item, key) => {
            if (!items.current[key]) {
              items.current[key] = React.createRef<HTMLLIElement>();
            }
            const hasChildren = item.child_items && item.child_items.length > 0;
            const megaMenuKey = hasChildren
              ? megaMenuOptionKeys[childMenuItems.indexOf(item)]
              : undefined;
            return (
              <MenuItem
                item={item}
                key={key}
                pageData={pageData}
                currentSubmenu={currentSubmenu}
                setCurrentSubmenu={setCurrentSubmenu}
                parentSubMenu={undefined}
                subMenu={item}
                tabIndex={key === focusedItem.current ? 0 : -1}
                itemRef={items.current[key]}
                controlParentMenu={controlMenu}
                megaMenuKey={megaMenuKey}
                megaMenuOption={
                  megaMenuKey ? themeNavigation?.[megaMenuKey] : undefined
                }
                megaMenuHighlights={
                  megaMenuKey ? megaMenuHighlights[megaMenuKey] || [] : []
                }
                topMenu={topMenu}
              />
            );
          })}
      </ul>
      {topMenu && topMenu.items.length > 0 && (
        <nav
          className={styles["mobile-utility-navigation"]}
          aria-label={topMenu.name}
        >
          <ul className={styles["mobile-utility-list"]}>
            {topMenu.items.map((topMenuItem) => (
              <li key={topMenuItem.ID ?? topMenuItem.url}>
                <CustomLink
                  href={topMenuItem.url}
                  target={topMenuItem.target}
                  onClick={() => setOpenMenu(false)}
                  disablePrefetch
                >
                  {topMenuItem.title}
                </CustomLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </>
  );
};
